import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import LifeCompass from '../components/LifeCompass';
import LifeMovieTimeline from '../components/LifeMovieTimeline';
import PredictiveNotifications from '../components/PredictiveNotifications';
import DynamicSky from '../components/DynamicSky';
import { TaraCard, TaraCardHeader, TaraCardTitle, TaraCardContent } from '../components/ui/TaraCard';
import TaraBadge from '../components/ui/TaraBadge';
import BottomNav from '../components/BottomNav';
import { Sparkles } from 'lucide-react';

const HomeInnovative = () => {
  // Birth Details = Source of Truth for name
  const birthRaw = localStorage.getItem('birthDetails') || localStorage.getItem('birth_details');
  const birthParsed = birthRaw ? (() => { try { return JSON.parse(birthRaw); } catch { return null; } })() : null;
  const userName = birthParsed?.name || localStorage.getItem('userName') || 'Friend';
  const navigate = useNavigate();
  
  // Determine time of day
  const hour = new Date().getHours();
  const timeOfDay = hour < 6 ? 'night' : hour < 12 ? 'morning' : hour < 17 ? 'afternoon' : hour < 21 ? 'evening' : 'night';
  const greeting = hour < 12 ? 'morning' : hour < 18 ? 'afternoon' : 'evening';

  // Get birth year for timeline
  const birthData = JSON.parse(localStorage.getItem('birthData') || '{}');
  const birthYear = birthData.date ? parseInt(birthData.date.split('-')[0]) : 1997;
  const currentAge = new Date().getFullYear() - birthYear;

  return (
    <div className="min-h-screen pb-24 relative overflow-hidden bg-gradient-to-br from-purple-900 via-pink-800 to-blue-900">
      {/* Animated Gradient Background */}
      <motion.div
        className="absolute inset-0 bg-gradient-to-br from-purple-600/30 via-pink-600/30 to-blue-600/30"
        animate={{
          background: [
            'linear-gradient(135deg, rgba(147,51,234,0.3) 0%, rgba(236,72,153,0.3) 50%, rgba(59,130,246,0.3) 100%)',
            'linear-gradient(135deg, rgba(236,72,153,0.3) 0%, rgba(59,130,246,0.3) 50%, rgba(147,51,234,0.3) 100%)',
            'linear-gradient(135deg, rgba(59,130,246,0.3) 0%, rgba(147,51,234,0.3) 50%, rgba(236,72,153,0.3) 100%)',
            'linear-gradient(135deg, rgba(147,51,234,0.3) 0%, rgba(236,72,153,0.3) 50%, rgba(59,130,246,0.3) 100%)',
          ]
        }}
        transition={{ duration: 15, repeat: Infinity, ease: 'linear' }}
      />
      
      {/* Floating Orbs */}
      {[...Array(5)].map((_, i) => (
        <motion.div
          key={i}
          className="absolute w-64 h-64 rounded-full blur-3xl"
          style={{
            background: i % 2 === 0 ? 'rgba(147,51,234,0.3)' : 'rgba(236,72,153,0.3)',
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
          }}
          animate={{
            x: [0, Math.random() * 100 - 50, 0],
            y: [0, Math.random() * 100 - 50, 0],
            scale: [1, 1.2, 1],
          }}
          transition={{
            duration: 10 + i * 2,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: i * 0.5,
          }}
        />
      ))}

      {/* Content */}
      <div className="relative z-10">
        {/* Header with greeting */}
        <motion.div 
          className="px-5 pt-12 pb-8"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="max-w-2xl mx-auto">
            <motion.h1 
              className="font-serif text-3xl md:text-4xl font-bold text-white drop-shadow-2xl"
              animate={{ scale: [1, 1.02, 1] }}
              transition={{ duration: 3, repeat: Infinity }}
            >
              <motion.span
                animate={{ rotate: [0, 10, -10, 0] }}
                transition={{ duration: 2, repeat: Infinity }}
                className="inline-block"
              >
                {timeOfDay === 'morning' && '☀️'} 
                {timeOfDay === 'afternoon' && '🌤️'}
                {timeOfDay === 'evening' && '🌅'}
                {timeOfDay === 'night' && '🌙'}
              </motion.span>
              {' '}Good {greeting}, {userName}
            </motion.h1>
            <motion.p 
              className="text-lg text-white/90 mt-2"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              Your cosmic intelligence dashboard
            </motion.p>
          </div>
        </motion.div>

        {/* Main Content */}
        <div className="px-5 py-6 max-w-5xl mx-auto space-y-6">
          {/* AI Agents CTA */}
          <motion.section
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.4, duration: 0.5 }}
          >
            <motion.button
              onClick={() => navigate('/ai-agents')}
              className="w-full bg-gradient-to-r from-cosmic-500 via-sacred-500 to-cosmic-500 text-white p-6 rounded-card shadow-lg transition-all duration-300 relative overflow-hidden"
              whileHover={{ scale: 1.02, boxShadow: '0 20px 60px rgba(138,43,226,0.3)' }}
              whileTap={{ scale: 0.98 }}
            >
              {/* Shimmer effect */}
              <motion.div
                className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                animate={{ x: ['-100%', '200%'] }}
                transition={{ duration: 3, repeat: Infinity, repeatDelay: 1 }}
              />
              
              <div className="flex items-center gap-4 relative z-10">
                <motion.div 
                  className="text-4xl"
                  animate={{ rotate: [0, 360] }}
                  transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
                >
                  🤖
                </motion.div>
                <div className="flex-1 text-left">
                  <h3 className="font-serif text-xl font-medium mb-1">
                    Meet Your 8 AI Agents
                  </h3>
                  <p className="text-sm text-white/90">
                    Specialized guides for every life domain • Available 24/7
                  </p>
                </div>
                <motion.div
                  animate={{ scale: [1, 1.2, 1], rotate: [0, 180, 360] }}
                  transition={{ duration: 4, repeat: Infinity }}
                >
                  <Sparkles className="w-6 h-6" />
                </motion.div>
              </div>
            </motion.button>
          </motion.section>

          {/* Predictive Notifications */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.5 }}
          >
            <h2 className="font-serif text-xl font-medium text-white mb-4 flex items-center gap-2">
              <motion.div
                animate={{ scale: [1, 1.2, 1], rotate: [0, 10, -10, 0] }}
                transition={{ duration: 3, repeat: Infinity }}
              >
                <Sparkles className="w-5 h-5 text-yellow-300" />
              </motion.div>
              Cosmic Guidance
            </h2>
            <PredictiveNotifications />
          </motion.section>

          {/* Life Compass */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.5 }}
          >
            <TaraCard variant="cosmic" className="p-6">
              <TaraCardHeader>
                <TaraCardTitle>Your Life Compass</TaraCardTitle>
                <p className="text-sm text-stone-600 mt-1">
                  Real-time balance across 8 life domains
                </p>
              </TaraCardHeader>
              <TaraCardContent>
                <LifeCompass
                  scores={{
                    health: 83,
                    career: 65,
                    relationships: 72,
                    wealth: 58,
                    family: 78,
                    education: 70,
                    spirituality: 55,
                    travel: 62,
                  }}
                />
                
                {/* Domain Legend */}
                <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-green-500" />
                    <span className="text-stone-700">Health: 83%</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-amber-500" />
                    <span className="text-stone-700">Career: 65%</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-pink-500" />
                    <span className="text-stone-700">Love: 72%</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-purple-500" />
                    <span className="text-stone-700">Wealth: 58%</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-cyan-500" />
                    <span className="text-stone-700">Family: 78%</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-indigo-500" />
                    <span className="text-stone-700">Skills: 70%</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-fuchsia-500" />
                    <span className="text-stone-700">Spirit: 55%</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-teal-500" />
                    <span className="text-stone-700">Travel: 62%</span>
                  </div>
                </div>
              </TaraCardContent>
            </TaraCard>
          </motion.section>

          {/* Life Movie Timeline */}
          <section>
            <TaraCard className="p-6 overflow-hidden">
              <TaraCardHeader>
                <TaraCardTitle>Your Life Movie</TaraCardTitle>
                <p className="text-sm text-stone-600 mt-1">
                  Major events and phases from birth to age 80
                </p>
              </TaraCardHeader>
              <TaraCardContent>
                <LifeMovieTimeline 
                  birthYear={birthYear}
                  currentAge={currentAge}
                />
              </TaraCardContent>
            </TaraCard>
          </section>

          {/* Quick Insights Grid */}
          <section>
            <h2 className="font-serif text-xl font-medium text-stone-900 mb-4">
              Today's Insights
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <TaraCard interactive onClick={() => navigate('/dashboard-new')}>
                <TaraCardContent className="p-5">
                  <div className="text-2xl mb-2">💪</div>
                  <h3 className="font-serif font-medium text-stone-900 mb-1">
                    Energy Peak
                  </h3>
                  <p className="text-sm text-stone-600">
                    4:00 PM - 6:00 PM
                  </p>
                  <TaraBadge variant="calm" size="sm" className="mt-2">
                    High
                  </TaraBadge>
                </TaraCardContent>
              </TaraCard>

              <TaraCard interactive onClick={() => navigate('/my-domains')}>
                <TaraCardContent className="p-5">
                  <div className="text-2xl mb-2">🧠</div>
                  <h3 className="font-serif font-medium text-stone-900 mb-1">
                    Mental Clarity
                  </h3>
                  <p className="text-sm text-stone-600">
                    Mercury well-placed
                  </p>
                  <TaraBadge variant="calm" size="sm" className="mt-2">
                    Good
                  </TaraBadge>
                </TaraCardContent>
              </TaraCard>

              <TaraCard interactive>
                <TaraCardContent className="p-5">
                  <div className="text-2xl mb-2">⚠️</div>
                  <h3 className="font-serif font-medium text-stone-900 mb-1">
                    Caution Window
                  </h3>
                  <p className="text-sm text-stone-600">
                    Avoid conflicts 8-10 PM
                  </p>
                  <TaraBadge variant="watchful" size="sm" className="mt-2">
                    Alert
                  </TaraBadge>
                </TaraCardContent>
              </TaraCard>
            </div>
          </section>
        </div>
      </div>

      {/* Bottom Navigation */}
      <BottomNav active="home" />
    </div>
  );
};

export default HomeInnovative;
